import { useEffect, useState } from "react";
import { DEFAULT_TEXT_MARGINS, POINTS_PER_INCH, readSelectionMargins } from "../services/marginOps";
import type { TextMargins } from "../../../types";
import styles from "./InlinePanel.module.css";

/** The four editable edges, in the order the panel lays them out. */
const EDGES = [
  { key: "left", label: "Left" },
  { key: "right", label: "Right" },
  { key: "top", label: "Top" },
  { key: "bottom", label: "Bottom" },
] as const satisfies ReadonlyArray<{ key: keyof TextMargins; label: string }>;

/** Inches, as the panel edits them — VSTO's FrmSetMargin worked in inches too. */
type MarginInches = Record<keyof TextMargins, string>;

const toInches = (points: number): string => String(Math.round((points / POINTS_PER_INCH) * 1000) / 1000);

function marginsToInches(margins: TextMargins): MarginInches {
  return {
    left: toInches(margins.left),
    top: toInches(margins.top),
    right: toInches(margins.right),
    bottom: toInches(margins.bottom),
  };
}

/** Parse an edited field; blank and malformed both read as 0, as in VSTO. */
const parseInches = (raw: string): number => {
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

interface MarginConfigPanelProps {
  readonly busy: boolean;
  readonly onCancel: () => void;
  /** Receives the four margins in **points**, ready for `setTextMargins`. */
  readonly onApply: (margins: TextMargins) => void;
}

/**
 * Set Margin panel — the port of VSTO's `FrmSetMargin` dialog.
 *
 * Opens showing the first selected shape's current margins (falling back to
 * PowerPoint's defaults when nothing readable is selected), and keeps VSTO's
 * three shortcuts: Zero, Make Same (copy the first non-zero edge onto the other
 * three) and Default.
 */
export function MarginConfigPanel({ busy, onCancel, onApply }: MarginConfigPanelProps): React.ReactElement {
  const [values, setValues] = useState<MarginInches>(() => marginsToInches(DEFAULT_TEXT_MARGINS));

  // Prefill from the live selection — best effort, so a host that can't report
  // margins just leaves the defaults in place.
  useEffect(() => {
    let live = true;
    void readSelectionMargins().then((current) => {
      if (live && current) setValues(marginsToInches(current));
    });
    return () => {
      live = false;
    };
  }, []);

  const setEdge = (edge: keyof TextMargins, raw: string): void =>
    setValues((prev) => ({ ...prev, [edge]: raw }));

  const setAll = (inches: number): void => setValues(marginsToInches({
    left: inches * POINTS_PER_INCH,
    top: inches * POINTS_PER_INCH,
    right: inches * POINTS_PER_INCH,
    bottom: inches * POINTS_PER_INCH,
  }));

  /** VSTO's "Make Same": the first non-zero edge (L → T → B → R) wins. */
  const makeSame = (): void => {
    const order: ReadonlyArray<keyof TextMargins> = ["left", "top", "bottom", "right"];
    const source = order.find((edge) => parseInches(values[edge]) > 0) ?? "right";
    setAll(parseInches(values[source]));
  };

  const apply = (): void =>
    onApply({
      left: parseInches(values.left) * POINTS_PER_INCH,
      top: parseInches(values.top) * POINTS_PER_INCH,
      right: parseInches(values.right) * POINTS_PER_INCH,
      bottom: parseInches(values.bottom) * POINTS_PER_INCH,
    });

  return (
    <fieldset className={styles.panel} aria-label="Set Margin options">
      <div className={styles.header}>
        <span className={styles.title}>Set Margin</span>
        <button type="button" className={styles.close} aria-label="Close" onClick={onCancel}>
          &times;
        </button>
      </div>

      {EDGES.map(({ key, label }) => (
        <div className={styles.row} key={key}>
          <label htmlFor={`sm-${key}`}>{label} (in)</label>
          <input
            id={`sm-${key}`}
            type="number"
            min={0}
            step={0.01}
            value={values[key]}
            disabled={busy}
            aria-label={`${label} margin in inches`}
            onChange={(e) => setEdge(key, e.target.value)}
          />
        </div>
      ))}

      <div className={styles.presets}>
        <button type="button" onClick={() => setAll(0)} disabled={busy}>
          Zero
        </button>
        <button type="button" onClick={makeSame} disabled={busy}>
          Make Same
        </button>
        <button type="button" onClick={() => setValues(marginsToInches(DEFAULT_TEXT_MARGINS))} disabled={busy}>
          Default
        </button>
      </div>

      <div className={styles.footer}>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className={styles.primary} onClick={apply} disabled={busy}>
          {busy ? "Working…" : "OK"}
        </button>
      </div>
    </fieldset>
  );
}
